import { useVirtualizer } from "@tanstack/react-virtual";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Columns3,
  Download,
  Eye,
  EyeOff,
  GripVertical,
  ImageOff,
  Info,
  LayoutGrid,
  ListFilter,
  Lock,
  Plus,
  RotateCcw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { buyboxOutcome, buyboxOutcomeLabel } from "@/lib/buybox";
import { api, type ProductOut } from "@/lib/api";
import { isBelowMargin } from "@/lib/margin";
import { formatDateTime, formatPercent, formatPrice } from "@/lib/format";
import { cn } from "@/lib/utils";

interface Props {
  products: ProductOut[];
  selectable?: boolean;
  onSelectionChange?: (products: ProductOut[]) => void;
}

const ROW_HEIGHT = 56;
const VIRTUAL_THRESHOLD = 200;
// Larghezza fissa della colonna checkbox (selectable): usata sia nel grid
// template sia come offset left della seconda cella sticky (SKU).
const SELECTION_COL_PX = 48;
// v2 (2026-07-08): riordino logico delle colonne. Il bump invalida i layout
// salvati in v1 (che conservavano il vecchio ordine) cosi' il nuovo ordine di
// default diventa effettivo senza dover premere "reset colonne". Le viste
// salvate (chiave separata) restano coi loro ordini espliciti.
const COLUMN_LAYOUT_STORAGE_KEY = "analisi-marketplace.products.column-layout.v2";
const COLUMN_LAYOUT_DRAFT_STORAGE_KEY = "analisi-marketplace.products.column-layout.draft.v2";
// v2: la colonna Fornitore ora filtra sul fornitore CANONICO ("Bizzotto") e non
// piu' sul brand grezzo ("BIZZOTTO"/"Yes"/"Homemotion"). Un filtro salvato con i
// vecchi valori non matcherebbe piu' nulla e la tabella risulterebbe vuota senza
// motivo apparente: il bump lo scarta invece di lasciarlo rompere in silenzio.
const FILTERS_STORAGE_KEY = "analisi-marketplace.products.filters.v2";
const FILTERS_DRAFT_STORAGE_KEY = "analisi-marketplace.products.filters.draft.v2";
const SAVED_VIEWS_STORAGE_KEY = "analisi-marketplace.products.saved-views.v1";
const MIN_COLUMN_WIDTH = 72;
const MAX_COLUMN_WIDTH = 720;

type ColumnKey =
  | "sku"
  | "ean"
  | "brand"
  | "vendor_brand"
  | "landed_cost"
  | "list_cost"
  | "discount_cost"
  | "shipping_estimate"
  | "shipping_cost_certified"
  | "shipping_cost_effective"
  | "current_fee_cost"
  | "current_total_cost"
  | "margin_floor"
  | "price_at_target_margin"
  | "my_price"
  | "current_margin"
  | "guadagno_eur"
  | "competitor_offer"
  | "buybox_target_price"
  | "buybox_target_margin"
  | "competitor_count"
  | "buybox_state"
  | "last_checked_at";

type ColumnGroup = "info" | "shipping" | "cost" | "mine" | "competitor" | "status";

type ColumnFilter = {
  query: string;
  selected: string[];
};

type Filters = Record<ColumnKey, ColumnFilter>;

type FilterOption = {
  value: string;
  label: string;
  count: number;
};

type ColumnDef = {
  key: ColumnKey;
  label: string;
  align: "left" | "right";
  placeholder: string;
  tooltip: string;
  helpText?: string[];
  group: ColumnGroup;
  filterValue: (product: ProductOut) => string;
  optionValue: (product: ProductOut) => string;
  exportValue: (product: ProductOut) => string;
};

const PRICING_STEPS = [
  "1. Prezzo vendita: il prezzo marketplace e' ivato.",
  "2. Ricavo netto: tolgo l'IVA del paese dal prezzo di vendita.",
  "3. Costo base: costo prodotto + spedizione stimata + imballo, tutti netti.",
  "4. Fee marketplace: prezzo vendita ivato x fee %.",
  "5. Costo totale: costo base + fee marketplace.",
  "6. Margine: ricavo netto - costo totale. Margine % = margine / ricavo netto.",
];

// Mappatura stato -> 2 esiti UI ("Buybox vinta" / "Buybox persa" / "—")
// centralizzata in @/lib/buybox (buyboxOutcome / buyboxOutcomeLabel), riusata
// da StatusBadge, dal filtro Stato e dai KPI.

// --- Group color-coding (Variante A: solo header tinto, corpo a zebra neutra) ---

const GROUP_HEADER_STYLE: Record<ColumnGroup, { bg?: string; bar?: string }> = {
  info: {},
  status: {},
  shipping: { bg: "hsl(var(--info) / 0.07)", bar: "hsl(var(--info))" },
  cost: { bg: "hsl(var(--warn-gfx) / 0.10)", bar: "hsl(var(--warn-gfx))" },
  mine: { bg: "hsl(var(--key) / 0.16)", bar: "hsl(var(--ink))" },
  competitor: { bg: "hsl(var(--ink-3) / 0.07)", bar: "hsl(var(--ink-3))" },
};

function groupHeaderStyle(group: ColumnGroup): CSSProperties {
  const s = GROUP_HEADER_STYLE[group];
  if (!s.bg && !s.bar) return {};
  return {
    backgroundColor: s.bg,
    borderTopWidth: s.bar ? "2px" : undefined,
    borderTopColor: s.bar,
    borderTopStyle: s.bar ? "solid" : undefined,
    borderRadius: 0,
  };
}

const COLUMNS = [
  {
    key: "sku",
    label: "SKU",
    align: "left",
    placeholder: "Filtra SKU",
    tooltip: "Codice prodotto e miniatura, letti da Poleepo. L'icona grafico apre lo storico prezzi.",
    helpText: ["Miniatura e identita' prodotto lette da Poleepo per lo SKU.", ...PRICING_STEPS],
    group: "info" as ColumnGroup,
    filterValue: (p) => p.sku,
    optionValue: (p) => p.sku,
    exportValue: (p) => p.sku,
  },
  {
    key: "ean",
    label: "EAN",
    align: "left",
    placeholder: "Filtra EAN",
    tooltip: "Codice a barre del prodotto (identificativo univoco internazionale).",
    group: "info" as ColumnGroup,
    filterValue: (p) => p.ean ?? "",
    optionValue: (p) => p.ean ?? "",
    exportValue: (p) => p.ean ?? "",
  },
  {
    key: "brand",
    label: "Fornitore",
    align: "left",
    placeholder: "Filtra",
    tooltip: "Fornitore del prodotto: determina i gate auto-apply per fornitore.",
    group: "info" as ColumnGroup,
    // `supplier` (risolto dal server), non `brand`: quest'ultimo arriva dal
    // catalogo Poleepo e per Bizzotto contiene il MARCHIO, per cui il filtro
    // elencava "Yes"/"Homemotion" accanto a "BIZZOTTO" come fossero fornitori
    // diversi. Il marchio resta visibile nella sua colonna dedicata.
    filterValue: (p) => p.supplier ?? p.brand ?? "",
    optionValue: (p) => p.supplier ?? p.brand ?? "",
    exportValue: (p) => p.supplier ?? p.brand ?? "",
  },
  {
    key: "vendor_brand",
    label: "Marchio",
    align: "left",
    placeholder: "Filtra",
    tooltip:
      "Marchio commerciale (feed Bizzotto): solo «Yes» è vendibile online sui marketplace; i non-Yes sono esclusi dall'auto-pricing.",
    group: "info" as ColumnGroup,
    filterValue: (p) => p.vendor_brand ?? "",
    optionValue: (p) => p.vendor_brand ?? "",
    exportValue: (p) => p.vendor_brand ?? "",
  },
  {
    key: "landed_cost",
    label: "Costo base",
    align: "right",
    placeholder: ">=, testo",
    tooltip: "Costo prodotto + spedizione + imballo, tutti al netto dell'IVA.",
    helpText: ["Qui vedi il punto 3: costo prodotto + spedizione + imballo.", ...PRICING_STEPS],
    group: "cost" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.landed_cost),
    optionValue: (p) => p.landed_cost ?? "",
    exportValue: (p) => p.landed_cost ?? "",
  },
  {
    key: "list_cost",
    label: "Costo listino",
    align: "right",
    placeholder: ">=, testo",
    tooltip:
      "Netto d'acquisto di listino (prezzo pieno fornitore) dal feed Stock-Hub, aggiornato ogni notte.",
    group: "cost" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.list_cost),
    optionValue: (p) => p.list_cost ?? "",
    exportValue: (p) => p.list_cost ?? "",
  },
  {
    key: "discount_cost",
    label: "Costo scontato",
    align: "right",
    placeholder: ">=, testo",
    tooltip:
      "Netto d'acquisto scontato (promo fornitore attiva) dal feed Stock-Hub. Vuoto = nessuna promo. Quando presente è il costo usato nei calcoli.",
    group: "cost" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.discount_cost),
    optionValue: (p) => p.discount_cost ?? "",
    exportValue: (p) => p.discount_cost ?? "",
  },
  {
    key: "shipping_estimate",
    label: "Stima spediz.",
    align: "right",
    placeholder: ">=, testo",
    tooltip: "Costo di spedizione stimato (manuale o da listino fornitore). Entra nel prezzo solo se mancano fattura e calcolo PIM: quella usata è in «Spedizione usata».",
    helpText: ["Stima del costo di spedizione (manuale o da listino fornitore)."],
    group: "shipping" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.shipping_estimate),
    optionValue: (p) => p.shipping_estimate ?? "",
    exportValue: (p) => p.shipping_estimate ?? "",
  },
  {
    key: "shipping_cost_certified",
    label: "Spediz. confermata",
    align: "right",
    placeholder: ">=, testo",
    tooltip: "Costo di spedizione reale certificato dalle fatture del corriere (mediana). Vuoto se non ancora disponibile.",
    helpText: [
      "Costo di spedizione reale confermato dalle fatture del corriere (mediana). Vuoto se non ancora disponibile.",
    ],
    group: "shipping" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.shipping_cost_certified),
    optionValue: (p) => p.shipping_cost_certified ?? "",
    exportValue: (p) => p.shipping_cost_certified ?? "",
  },
  {
    key: "shipping_cost_effective",
    label: "Spedizione usata",
    align: "right",
    placeholder: "calcolo PIM, manca…",
    tooltip:
      "La spedizione che entra davvero nel prezzo, e da dove viene: fattura del corriere, calcolo PIM (peso e misure), listino fornitore o BRT per l'estero. «manca» = nessun prezzo automatico.",
    helpText: [
      "Spedizione usata dal calcolo del prezzo, con la fonte: fattura > calcolo PIM > listino in Italia, solo BRT all'estero.",
      "«manca»: senza spedizione il prezzo non si calcola.",
    ],
    group: "shipping" as ColumnGroup,
    filterValue: (p) =>
      `${priceSearchValue(p.shipping_cost_effective)} ${SHIPPING_SOURCE_LABEL[p.shipping_cost_source]}`,
    // Il menu elenca le fonti, non centinaia di importi: serve a trovare i "manca".
    optionValue: (p) => SHIPPING_SOURCE_LABEL[p.shipping_cost_source],
    exportValue: (p) => p.shipping_cost_effective ?? "",
  },
  {
    key: "current_fee_cost",
    label: "Fee att.",
    align: "right",
    placeholder: "Fee",
    tooltip: "Commissione del marketplace calcolata sul prezzo di vendita ivato attuale.",
    helpText: ["Qui vedi il punto 4: fee calcolata sul prezzo ivato di vendita.", ...PRICING_STEPS],
    group: "cost" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.current_fee_cost),
    optionValue: (p) => p.current_fee_cost ?? "",
    exportValue: (p) => p.current_fee_cost ?? "",
  },
  {
    key: "current_total_cost",
    label: "Costo tot.",
    align: "right",
    placeholder: "Totale",
    tooltip: "Costo base + fee marketplace: quanto ti costa davvero vendere a questo prezzo.",
    helpText: ["Qui vedi il punto 5: costo base + fee marketplace.", ...PRICING_STEPS],
    group: "cost" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.current_total_cost),
    optionValue: (p) => p.current_total_cost ?? "",
    exportValue: (p) => p.current_total_cost ?? "",
  },
  {
    key: "margin_floor",
    label: "Prezzo minimo",
    align: "right",
    placeholder: ">=, testo",
    tooltip: "Il prezzo più basso a cui puoi vendere restando sopra il margine minimo, calcolato dai tuoi costi. Cambia se modifichi il margine minimo nelle Impostazioni del marketplace.",
    group: "mine" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.margin_floor),
    optionValue: (p) => p.margin_floor ?? "",
    exportValue: (p) => p.margin_floor ?? "",
  },
  {
    key: "price_at_target_margin",
    label: "PREZZO", // sovrascritta a runtime con la % del margine obiettivo
    align: "right",
    placeholder: ">=, testo",
    tooltip:
      "Il prezzo che AM imposta in autonomia per raggiungere il margine obiettivo, calcolato dai tuoi costi. Cambia se modifichi il margine obiettivo nelle Impostazioni.",
    group: "mine" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.price_at_target_margin),
    optionValue: (p) => p.price_at_target_margin ?? "",
    exportValue: (p) => p.price_at_target_margin ?? "",
  },
  {
    key: "my_price",
    label: "Mio prezzo",
    align: "right",
    placeholder: "Prezzo",
    tooltip: "Il tuo prezzo di vendita ivato attuale su questo marketplace.",
    helpText: ["Qui il prezzo usato nel calcolo e' il mio prezzo vendita ivato.", ...PRICING_STEPS],
    group: "mine" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.my_total_price ?? p.my_price),
    optionValue: (p) => p.my_total_price ?? p.my_price ?? "",
    exportValue: (p) => p.my_total_price ?? p.my_price ?? "",
  },
  {
    key: "current_margin",
    label: "Margine att.",
    align: "right",
    placeholder: "Margine",
    tooltip: "Guadagno netto e percentuale al tuo prezzo attuale (ricavo netto − costo totale).",
    helpText: ["Qui vedi il punto 6 applicato al mio prezzo attuale.", ...PRICING_STEPS],
    group: "mine" as ColumnGroup,
    filterValue: (p) =>
      `${priceSearchValue(p.current_margin)} ${formatPercent(p.current_margin_percent)}`,
    optionValue: (p) => marginLabel(p.current_margin, p.current_margin_percent),
    exportValue: (p) => marginLabel(p.current_margin, p.current_margin_percent),
  },
  {
    key: "guadagno_eur",
    label: "Guadagno €",
    align: "right",
    placeholder: ">=, testo",
    tooltip: "Guadagno in euro al prezzo attuale (spedizione inclusa). Rosso se in perdita.",
    helpText: ["Guadagno in € al prezzo attuale (= margine att., spedizione inclusa). Rosso se in perdita."],
    group: "mine" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.current_margin),
    optionValue: (p) => p.current_margin ?? "",
    exportValue: (p) => p.current_margin ?? "",
  },
  {
    key: "competitor_offer",
    label: "Offerta competitor",
    align: "right",
    placeholder: "Prezzo/shop",
    tooltip: "Miglior prezzo del concorrente e il suo shop. 'tot.' include la sua spedizione quando diversa.",
    group: "competitor" as ColumnGroup,
    filterValue: (p) =>
      [
        priceSearchValue(p.min_competitor_price),
        priceSearchValue(p.min_competitor_total),
        p.min_competitor_shop_name ?? p.min_competitor_shop_id ?? "",
      ].join(" "),
    optionValue: (p) =>
      [
        p.min_competitor_price ?? p.min_competitor_total ?? "",
        p.min_competitor_shop_name ?? p.min_competitor_shop_id ?? "",
      ]
        .filter(Boolean)
        .join(" / "),
    exportValue: (p) =>
      [
        p.min_competitor_price ?? p.min_competitor_total ?? "",
        p.min_competitor_shop_name ?? p.min_competitor_shop_id ?? "",
      ]
        .filter(Boolean)
        .join(" / "),
  },
  {
    key: "buybox_target_price",
    label: "Target -1",
    align: "right",
    placeholder: "Prezzo",
    tooltip: "Prezzo per riprendere la buybox: miglior competitor totale − 1 €.",
    helpText: [
      "Prezzo target = miglior competitor totale - 1 euro; poi ricalcolo tutti i 6 punti sul target.",
      ...PRICING_STEPS,
    ],
    group: "competitor" as ColumnGroup,
    filterValue: (p) => priceSearchValue(p.buybox_target_price),
    optionValue: (p) => p.buybox_target_price ?? "",
    exportValue: (p) => p.buybox_target_price ?? "",
  },
  {
    key: "buybox_target_margin",
    label: "Margine target",
    align: "right",
    placeholder: "Margine",
    tooltip: "Guadagno netto e percentuale se vendessi al prezzo target (−1 € sul competitor).",
    helpText: ["Qui vedi il punto 6 applicato al prezzo target buybox.", ...PRICING_STEPS],
    group: "competitor" as ColumnGroup,
    filterValue: (p) =>
      `${priceSearchValue(p.buybox_target_margin)} ${formatPercent(
        p.buybox_target_margin_percent,
      )}`,
    optionValue: (p) => marginLabel(p.buybox_target_margin, p.buybox_target_margin_percent),
    exportValue: (p) => marginLabel(p.buybox_target_margin, p.buybox_target_margin_percent),
  },
  {
    key: "competitor_count",
    label: "# comp.",
    align: "right",
    placeholder: "N.",
    tooltip: "Numero di concorrenti rilevati su questo marketplace.",
    group: "competitor" as ColumnGroup,
    filterValue: (p) => String(p.competitor_count ?? ""),
    optionValue: (p) => String(p.competitor_count ?? ""),
    exportValue: (p) => String(p.competitor_count ?? ""),
  },
  {
    key: "buybox_state",
    label: "Stato",
    align: "left",
    placeholder: "Stato",
    tooltip: "Esito buybox: vinta (hai il prezzo migliore) o persa. '—' se non vendi questo prodotto qui.",
    group: "status" as ColumnGroup,
    filterValue: (p) => buyboxOutcomeLabel(buyboxOutcome(p)),
    optionValue: (p) => buyboxOutcomeLabel(buyboxOutcome(p)),
    exportValue: (p) => buyboxOutcomeLabel(buyboxOutcome(p)),
  },
  {
    key: "last_checked_at",
    label: "Ultimo check",
    align: "left",
    placeholder: "Data/ora",
    tooltip: "Data e ora dell'ultimo controllo prezzi su questo marketplace.",
    group: "status" as ColumnGroup,
    filterValue: (p) => `${p.last_checked_at ?? ""} ${formatDateTime(p.last_checked_at)}`,
    optionValue: (p) => formatDateTime(p.last_checked_at),
    exportValue: (p) => formatDateTime(p.last_checked_at),
  },
] satisfies ColumnDef[];

// Ordine LOGICO di default delle colonne (guida `defaultLayout()`): l'ordine
// dell'array COLUMNS resta solo registro delle definizioni, non l'ordine di
// display. Flusso di lettura naturale:
//   Identita' -> Costi (build-up: acquisto -> spedizione -> base -> fee ->
//   totale) -> I miei prezzi/margini -> Competitor -> Stato.
// L'append difensivo aggiunge in coda eventuali colonne nuove non ancora
// ordinate qui (nessuna colonna sparisce se ne aggiungi una a COLUMNS).
const LOGICAL_COLUMN_ORDER: ColumnKey[] = [
  // Identita' prodotto
  "sku",
  "ean",
  "brand",
  "vendor_brand",
  // Costi, in ordine di formazione
  "list_cost",
  "discount_cost",
  "shipping_estimate",
  "shipping_cost_certified",
  "shipping_cost_effective",
  "landed_cost",
  "current_fee_cost",
  "current_total_cost",
  // I miei prezzi e margini
  "my_price",
  "margin_floor",
  "price_at_target_margin",
  "current_margin",
  "guadagno_eur",
  // Competitor e buybox
  "competitor_offer",
  "competitor_count",
  "buybox_target_price",
  "buybox_target_margin",
  // Stato
  "buybox_state",
  "last_checked_at",
];
const DEFAULT_COLUMN_KEYS: ColumnKey[] = [
  ...LOGICAL_COLUMN_ORDER,
  ...COLUMNS.map((column) => column.key).filter((key) => !LOGICAL_COLUMN_ORDER.includes(key)),
];

const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  sku: 228,
  ean: 142,
  brand: 132,
  vendor_brand: 120,
  landed_cost: 132,
  list_cost: 132,
  discount_cost: 140,
  shipping_estimate: 130,
  shipping_cost_certified: 150,
  shipping_cost_effective: 150,
  current_fee_cost: 128,
  current_total_cost: 136,
  margin_floor: 132,
  price_at_target_margin: 136,
  my_price: 132,
  current_margin: 150,
  guadagno_eur: 130,
  competitor_offer: 210,
  buybox_target_price: 128,
  buybox_target_margin: 160,
  competitor_count: 96,
  buybox_state: 164,
  last_checked_at: 160,
};

type ColumnLayout = {
  order: ColumnKey[];
  hidden: ColumnKey[];
  widths: Partial<Record<ColumnKey, number>>;
};

type SavedView = {
  id: string;
  name: string;
  layout: ColumnLayout;
  filters: Filters;
  createdAt: string;
};

function defaultLayout(): ColumnLayout {
  return { order: DEFAULT_COLUMN_KEYS, hidden: [], widths: {} };
}

function resolveWidth(
  key: ColumnKey,
  widths: Partial<Record<ColumnKey, number>>,
  autoFit?: Partial<Record<ColumnKey, number>>,
): number {
  const override = widths[key];
  if (typeof override === "number" && Number.isFinite(override)) {
    return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, override));
  }
  const fit = autoFit?.[key];
  if (typeof fit === "number" && Number.isFinite(fit)) {
    return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, fit));
  }
  return DEFAULT_COLUMN_WIDTHS[key];
}

// Heuristica auto-fit: width = max(header label, max cell content) +
// padding/icone, rispettando MIN/MAX. Cap a 60 char per non far esplodere
// colonne con descrizioni lunghissime. Sample primi 200 prodotti per perf.
function computeAutoFitWidths(
  columns: ColumnDef[],
  products: ProductOut[],
): Partial<Record<ColumnKey, number>> {
  // Overhead REALE dell'header: handle 16 + gap 4 + ⓘ 14 + gap 4 + padding
  // cella 24 (=62) + dentro il chip: padding 20 + funnel 14 + gap 6 + gap 8 +
  // chevron 14 (=62). Il vecchio 88 sottostimava di ~36px: il testo sbordava
  // dal chip (mascherato dall'overflow visibile, bug sormonto 2026-07-02).
  const HEADER_OVERHEAD = 124;
  const CELL_PADDING = 28;
  const CHAR_W = 7.2;
  const out: Partial<Record<ColumnKey, number>> = {};
  const sample = products.slice(0, 200);
  for (const column of columns) {
    // Larghezza header = PAROLA più lunga (il label wrappa per-parola su max
    // 2 righe con line-clamp): tutti i label sono 1-2 parole, quindi 2 righe
    // bastano sempre e le colonne non si allargano per label lunghi.
    const longestWord = column.label
      .split(/\s+/)
      .reduce((max, word) => Math.max(max, word.length), 0);
    const headerW = longestWord * CHAR_W + HEADER_OVERHEAD;
    let maxCellChars = 0;
    for (const p of sample) {
      const len = String(column.exportValue(p) ?? "").length;
      if (len > maxCellChars) maxCellChars = len;
      if (maxCellChars > 60) break;
    }
    let width = Math.max(headerW, maxCellChars * CHAR_W + CELL_PADDING);
    if (column.key === "sku") width += 64; // immagine miniatura + gap
    out[column.key] = Math.round(
      Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)),
    );
  }
  return out;
}

function createEmptyFilters(): Filters {
  return COLUMNS.reduce((acc, column) => {
    acc[column.key] = { query: "", selected: [] };
    return acc;
  }, {} as Filters);
}

function loadFilters(): Filters {
  if (typeof window === "undefined") {
    return createEmptyFilters();
  }

  const draft = readStoredJson(window.sessionStorage, FILTERS_DRAFT_STORAGE_KEY);
  if (draft !== null) return normalizeFilters(draft);
  const saved = readStoredJson(window.localStorage, FILTERS_STORAGE_KEY);
  if (saved !== null) return normalizeFilters(saved);
  return createEmptyFilters();
}

function persistFilters(filters: Filters): void {
  if (typeof window === "undefined") return;
  writeStoredJson(window.sessionStorage, FILTERS_DRAFT_STORAGE_KEY, filters);
}

function saveFilters(filters: Filters): void {
  if (typeof window === "undefined") return;
  writeStoredJson(window.localStorage, FILTERS_STORAGE_KEY, filters);
  writeStoredJson(window.sessionStorage, FILTERS_DRAFT_STORAGE_KEY, filters);
}

function loadColumnLayout(): ColumnLayout {
  if (typeof window === "undefined") {
    return defaultLayout();
  }

  const draft = readStoredJson(window.sessionStorage, COLUMN_LAYOUT_DRAFT_STORAGE_KEY);
  if (draft !== null) return normalizeColumnLayout(draft);
  const saved = readStoredJson(window.localStorage, COLUMN_LAYOUT_STORAGE_KEY);
  if (saved !== null) return normalizeColumnLayout(saved);
  return defaultLayout();
}

function persistColumnLayout(layout: ColumnLayout): void {
  if (typeof window === "undefined") return;
  writeStoredJson(window.sessionStorage, COLUMN_LAYOUT_DRAFT_STORAGE_KEY, layout);
}

function saveColumnLayout(layout: ColumnLayout): void {
  if (typeof window === "undefined") return;
  writeStoredJson(window.localStorage, COLUMN_LAYOUT_STORAGE_KEY, layout);
  writeStoredJson(window.sessionStorage, COLUMN_LAYOUT_DRAFT_STORAGE_KEY, layout);
}

function normalizeColumnLayout(value: unknown): ColumnLayout {
  if (!value || typeof value !== "object") return defaultLayout();
  const candidate = value as { order?: unknown; hidden?: unknown; widths?: unknown };
  const order = normalizeColumnOrder(Array.isArray(candidate.order) ? candidate.order : []);
  const hidden = Array.isArray(candidate.hidden)
    ? uniqueColumnKeys(candidate.hidden).filter((key) => order.includes(key))
    : [];
  const widths: Partial<Record<ColumnKey, number>> = {};
  if (candidate.widths && typeof candidate.widths === "object") {
    for (const [key, value] of Object.entries(candidate.widths as Record<string, unknown>)) {
      if (!isColumnKey(key)) continue;
      const num = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(num)) continue;
      widths[key] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, num));
    }
  }
  return {
    order,
    hidden: hidden.length >= DEFAULT_COLUMN_KEYS.length ? hidden.slice(0, -1) : hidden,
    widths,
  };
}

function loadSavedViews(): SavedView[] {
  if (typeof window === "undefined") return [];
  const raw = readStoredJson(window.localStorage, SAVED_VIEWS_STORAGE_KEY);
  return parseSavedViewsList(raw);
}

function parseSavedViewsList(raw: unknown): SavedView[] {
  if (!Array.isArray(raw)) return [];
  const views: SavedView[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const candidate = entry as Record<string, unknown>;
    const id = typeof candidate.id === "string" ? candidate.id : null;
    const name = typeof candidate.name === "string" ? candidate.name : null;
    if (!id || !name) continue;
    views.push({
      id,
      name,
      layout: normalizeColumnLayout(candidate.layout),
      filters: normalizeFilters(candidate.filters),
      createdAt:
        typeof candidate.createdAt === "string" ? candidate.createdAt : new Date().toISOString(),
    });
  }
  return views;
}

function saveSavedViews(views: SavedView[]): void {
  if (typeof window === "undefined") return;
  const serialized = JSON.stringify(views);
  try {
    window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, serialized);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Salvataggio vista (localStorage) fallito:", err);
    if (typeof window.alert === "function") {
      window.alert(
        "Impossibile salvare la vista in locale (localStorage pieno o non disponibile). " +
          "Provo backup server-side.",
      );
    }
  }
  // Backup server-side fire-and-forget per resistere a flush localStorage
  // del WebView (#3 vista sparita ricorrente). Il primary store resta
  // localStorage per latenza/offline; il server e' fallback durabile.
  api.putSavedViewsBackup(serialized).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("Backup vista server-side fallito:", err);
  });
}

function makeViewId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeFilters(value: unknown): Filters {
  const base = createEmptyFilters();
  if (!value || typeof value !== "object") return base;
  const candidate = value as Partial<Record<ColumnKey, unknown>>;
  for (const key of DEFAULT_COLUMN_KEYS) {
    const raw = candidate[key];
    if (!raw || typeof raw !== "object") continue;
    const filter = raw as { query?: unknown; selected?: unknown };
    base[key] = {
      query: typeof filter.query === "string" ? filter.query : "",
      selected: Array.isArray(filter.selected)
        ? filter.selected.filter((item): item is string => typeof item === "string")
        : [],
    };
  }
  return base;
}

// Una vista salvata prima di una colonna nuova non la conosce: la colonna va
// dopo la sua vicina logica gia' presente (in testa se non ce n'e'), non in
// coda dove nessuno la vede.
function normalizeColumnOrder(values: unknown[]): ColumnKey[] {
  const order = uniqueColumnKeys(values);
  DEFAULT_COLUMN_KEYS.forEach((key, index) => {
    if (order.includes(key)) return;
    const previous = DEFAULT_COLUMN_KEYS.slice(0, index)
      .reverse()
      .find((candidate) => order.includes(candidate));
    order.splice(previous ? order.indexOf(previous) + 1 : 0, 0, key);
  });
  return order;
}

function uniqueColumnKeys(values: unknown[]): ColumnKey[] {
  const seen = new Set<ColumnKey>();
  const keys: ColumnKey[] = [];
  for (const value of values) {
    if (!isColumnKey(value) || seen.has(value)) continue;
    seen.add(value);
    keys.push(value);
  }
  return keys;
}

function isColumnKey(value: unknown): value is ColumnKey {
  return typeof value === "string" && DEFAULT_COLUMN_KEYS.includes(value as ColumnKey);
}

function getColumnsFromOrder(order: ColumnKey[]): ColumnDef[] {
  return order
    .map((key) => COLUMNS.find((column) => column.key === key))
    .filter(Boolean) as ColumnDef[];
}

function reorderColumns(order: ColumnKey[], source: ColumnKey, target: ColumnKey): ColumnKey[] {
  if (source === target) return order;
  const next = order.filter((k) => k !== source);
  const ti = next.indexOf(target);
  if (ti < 0) return order;
  next.splice(ti, 0, source);
  return next;
}

// Altezza del container di scroll ancorata alla viewport: il vecchio h-[70vh]
// fisso spingeva il fondo (dove vive la scrollbar ORIZZONTALE) sotto la piega
// quando il contenuto sopra la tabella supera il 30vh — scrollbar X invisibile
// e irraggiungibile (bug 2026-07-02: "non posso spostarmi tra le colonne").
function useViewportFillHeight(
  ref: RefObject<HTMLDivElement | null>,
  { min = 320, margin = 24 }: { min?: number; margin?: number } = {},
): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setHeight(Math.max(min, Math.round(window.innerHeight - top - margin)));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [ref, min, margin]);
  return height;
}

function getGridTemplate(
  columns: ColumnDef[],
  widths: Partial<Record<ColumnKey, number>>,
  autoFit?: Partial<Record<ColumnKey, number>>,
): string {
  return columns.map((column) => `${resolveWidth(column.key, widths, autoFit)}px`).join(" ");
}

function getTableMinWidth(
  columns: ColumnDef[],
  widths: Partial<Record<ColumnKey, number>>,
  autoFit?: Partial<Record<ColumnKey, number>>,
): string {
  const width = columns.reduce(
    (sum, column) => sum + resolveWidth(column.key, widths, autoFit),
    0,
  );
  return `${Math.max(width, 320)}px`;
}

function addSelectionWidth(width: string): string {
  const numeric = Number.parseInt(width, 10);
  return `${Number.isFinite(numeric) ? numeric + 48 : 368}px`;
}

function readStoredJson(storage: Storage, key: string): unknown | null {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredJson(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // View persistence is a convenience; the table still works without storage.
  }
}

export function ProductsTable(props: Props): JSX.Element {
  return <ProductsTableInner {...props} />;
}

export function SelectableProductsTable({
  products,
  onSelectionChange,
}: {
  products: ProductOut[];
  onSelectionChange: (products: ProductOut[]) => void;
}): JSX.Element {
  return <ProductsTableInner products={products} selectable onSelectionChange={onSelectionChange} />;
}

function ProductsTableInner({
  products,
  selectable = false,
  onSelectionChange,
}: Props): JSX.Element {
  const [filters, setFilters] = useState<Filters>(() => loadFilters());
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(() => new Set());
  const [openFilter, setOpenFilter] = useState<ColumnKey | null>(null);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [viewsMenuOpen, setViewsMenuOpen] = useState(false);
  const [columnLayout, setColumnLayout] = useState<ColumnLayout>(() => loadColumnLayout());
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews());
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewStatus, setViewStatus] = useState<string | null>(null);
  // Canali price-setter (BricoBravo): la colonna "PREZZO (x%)" compare solo se i
  // prodotti sono di un canale a margine, con la % presa dal margine obiettivo.
  const priceSetterPct = useMemo(() => {
    const row = products.find((p) => p.is_price_setter);
    return row?.target_margin_percent ?? null;
  }, [products]);
  const orderedColumns = useMemo(() => {
    const cols = getColumnsFromOrder(columnLayout.order);
    return cols
      .filter((c) => c.key !== "price_at_target_margin" || priceSetterPct !== null)
      .map((c) =>
        c.key === "price_at_target_margin" && priceSetterPct !== null
          ? { ...c, label: `PREZZO (${formatTargetMarginLabel(priceSetterPct)})` }
          : c,
      );
  }, [columnLayout.order, priceSetterPct]);
  const hiddenColumns = useMemo(() => new Set(columnLayout.hidden), [columnLayout.hidden]);
  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => !hiddenColumns.has(column.key)),
    [hiddenColumns, orderedColumns],
  );
  const filteredProductsForFit = useMemo(
    () => products.filter((product) => productMatchesFilters(product, filters)),
    [filters, products],
  );
  const autoFitCacheRef = useRef<Map<string, Partial<Record<ColumnKey, number>>>>(new Map());
  const autoFitCacheKey = useMemo(
    // La % del margine obiettivo entra nella chiave: la label dinamica
    // "PREZZO (x%)" cambia la larghezza calcolata, la cache non deve confondere
    // due percentuali diverse a parità di colonne visibili.
    () => `${[...visibleColumns.map((c) => c.key)].sort().join("|")}|pct=${priceSetterPct ?? ""}`,
    [visibleColumns, priceSetterPct],
  );
  const autoFitWidths = useMemo<Partial<Record<ColumnKey, number>>>(() => {
    const cache = autoFitCacheRef.current;
    const cached = cache.get(autoFitCacheKey);
    if (cached) return cached;
    if (filteredProductsForFit.length === 0) return {}; // non congelare su set vuoto
    const widths = computeAutoFitWidths(visibleColumns, filteredProductsForFit);
    cache.set(autoFitCacheKey, widths);
    return widths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFitCacheKey, filteredProductsForFit]);
  const gridTemplate = useMemo(
    () => getGridTemplate(visibleColumns, columnLayout.widths, autoFitWidths),
    [visibleColumns, columnLayout.widths, autoFitWidths],
  );
  const tableMinWidth = useMemo(
    () => getTableMinWidth(visibleColumns, columnLayout.widths, autoFitWidths),
    [visibleColumns, columnLayout.widths, autoFitWidths],
  );
  const setColumnWidth = useCallback((key: ColumnKey, width: number) => {
    setColumnLayout((current) => {
      const next = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)));
      return { ...current, widths: { ...current.widths, [key]: next } };
    });
  }, []);
  const resetColumnWidth = useCallback((key: ColumnKey) => {
    setColumnLayout((current) => {
      if (current.widths[key] === undefined) return current;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _removed, ...rest } = current.widths;
      return { ...current, widths: rest };
    });
  }, []);
  const filterOptions = useMemo(() => buildFilterOptions(products), [products]);
  const filteredProducts = filteredProductsForFit;
  const selectedProducts = useMemo(
    () => products.filter((product) => selectedSkus.has(product.sku)),
    [products, selectedSkus],
  );
  const selectionGridTemplate = selectable
    ? `${SELECTION_COL_PX}px ${gridTemplate}`
    : gridTemplate;
  const selectionTableMinWidth = selectable ? addSelectionWidth(tableMinWidth) : tableMinWidth;
  const selectableVisibleSkus = useMemo(
    () => filteredProducts.map((product) => product.sku),
    [filteredProducts],
  );
  const selectedVisibleCount = selectableVisibleSkus.filter((sku) => selectedSkus.has(sku)).length;
  const allVisibleSelected =
    selectableVisibleSkus.length > 0 && selectedVisibleCount === selectableVisibleSkus.length;
  const hasFilters = Object.values(filters).some(
    (filter) => filter.query || filter.selected.length > 0,
  );

  const setFilterQuery = (key: ColumnKey, value: string) => {
    setFilters((current) => ({ ...current, [key]: { ...current[key], query: value } }));
  };

  const toggleFilterValue = (key: ColumnKey, value: string) => {
    setFilters((current) => {
      const selected = current[key].selected.includes(value)
        ? current[key].selected.filter((item) => item !== value)
        : [...current[key].selected, value];
      return { ...current, [key]: { ...current[key], selected } };
    });
  };

  const selectFilterValues = (key: ColumnKey, values: string[]) => {
    setFilters((current) => {
      const merged = Array.from(new Set([...current[key].selected, ...values]));
      return { ...current, [key]: { ...current[key], selected: merged } };
    });
  };

  const clearColumnFilter = (key: ColumnKey) => {
    setFilters((current) => ({ ...current, [key]: { query: "", selected: [] } }));
  };

  const clearFilters = () => setFilters(createEmptyFilters());

  const toggleProductSelection = (sku: string) => {
    setSelectedSkus((current) => {
      const next = new Set(current);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  const toggleVisibleSelection = () => {
    setSelectedSkus((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        for (const sku of selectableVisibleSkus) next.delete(sku);
      } else {
        for (const sku of selectableVisibleSkus) next.add(sku);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedSkus(new Set());

  const toggleColumnVisibility = (key: ColumnKey) => {
    setColumnLayout((current) => {
      const hidden = new Set(current.hidden);
      if (hidden.has(key)) {
        hidden.delete(key);
      } else if (DEFAULT_COLUMN_KEYS.length - hidden.size > 1) {
        hidden.add(key);
      }
      return { ...current, hidden: Array.from(hidden) };
    });
  };

  const moveColumn = (key: ColumnKey, direction: -1 | 1) => {
    setColumnLayout((current) => {
      const order = normalizeColumnOrder(current.order);
      const index = order.indexOf(key);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= order.length) return current;
      const next = [...order];
      [next[index], next[target]] = [next[target], next[index]];
      return { ...current, order: next };
    });
  };

  const reorderColumn = useCallback((source: ColumnKey, target: ColumnKey) => {
    setColumnLayout((current) => ({
      ...current,
      order: reorderColumns(normalizeColumnOrder(current.order), source, target),
    }));
  }, []);

  const resetColumnLayout = () => {
    setColumnLayout(defaultLayout());
    setActiveViewId(null);
  };

  const persistCurrentAsDefault = useCallback(() => {
    saveColumnLayout(columnLayout);
    saveFilters(filters);
    setViewStatus("Vista corrente salvata come predefinita");
  }, [columnLayout, filters]);

  const saveCurrentAsView = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const now = new Date().toISOString();
      const existingMatch = savedViews.find(
        (view) => view.name.toLowerCase() === trimmed.toLowerCase(),
      );
      // Conferma esplicita per evitare overwrite silenzioso (#3): in
      // passato l'utente perdeva una vista creando un secondo "salva"
      // con stesso nome.
      if (
        existingMatch &&
        !window.confirm(
          `Esiste gia' una vista chiamata "${existingMatch.name}". Sovrascriverla?`,
        )
      ) {
        return;
      }
      setSavedViews((current) => {
        const existingIndex = current.findIndex(
          (view) => view.name.toLowerCase() === trimmed.toLowerCase(),
        );
        if (existingIndex >= 0) {
          const existing = current[existingIndex];
          const updated: SavedView = {
            ...existing,
            layout: columnLayout,
            filters,
            createdAt: existing.createdAt, // preserva data creazione originale
          };
          setActiveViewId(existing.id);
          const next = [...current];
          next[existingIndex] = updated;
          saveSavedViews(next);
          return next;
        }
        const view: SavedView = {
          id: makeViewId(),
          name: trimmed,
          layout: columnLayout,
          filters,
          createdAt: now,
        };
        setActiveViewId(view.id);
        const next = [...current, view].sort((a, b) => a.name.localeCompare(b.name, "it"));
        saveSavedViews(next);
        return next;
      });
      setViewStatus(
        existingMatch ? `Vista "${trimmed}" aggiornata` : `Vista "${trimmed}" salvata`,
      );
    },
    [columnLayout, filters, savedViews],
  );

  // #5: aggiornamento esplicito della vista attiva (senza riscrivere il
  // nome). Disabilitato se nessuna vista e' stata caricata.
  const updateActiveView = useCallback(() => {
    if (!activeViewId) return;
    setSavedViews((current) => {
      const idx = current.findIndex((view) => view.id === activeViewId);
      if (idx < 0) return current;
      const next = [...current];
      next[idx] = {
        ...next[idx],
        layout: columnLayout,
        filters,
      };
      saveSavedViews(next);
      setViewStatus(`Vista "${next[idx].name}" aggiornata`);
      return next;
    });
  }, [activeViewId, columnLayout, filters]);

  const loadView = useCallback((view: SavedView) => {
    setColumnLayout(view.layout);
    setFilters(view.filters);
    setActiveViewId(view.id);
    setViewStatus(`Vista "${view.name}" caricata`);
    setViewsMenuOpen(false);
  }, []);

  const deleteView = useCallback(
    (id: string) => {
      setSavedViews((current) => {
        const next = current.filter((view) => view.id !== id);
        saveSavedViews(next);
        return next;
      });
      setActiveViewId((current) => (current === id ? null : current));
      setViewStatus("Vista eliminata");
    },
    [],
  );

  const renameView = useCallback((id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSavedViews((current) => {
      const next = current
        .map((view) => (view.id === id ? { ...view, name: trimmed } : view))
        .sort((a, b) => a.name.localeCompare(b.name, "it"));
      saveSavedViews(next);
      return next;
    });
  }, []);

  useEffect(() => {
    persistColumnLayout(columnLayout);
  }, [columnLayout]);

  useEffect(() => {
    persistFilters(filters);
  }, [filters]);

  useEffect(() => {
    setSelectedSkus((current) => {
      const valid = new Set(products.map((product) => product.sku));
      const next = new Set(Array.from(current).filter((sku) => valid.has(sku)));
      return next.size === current.size ? current : next;
    });
  }, [products]);

  useEffect(() => {
    onSelectionChange?.(selectedProducts);
  }, [onSelectionChange, selectedProducts]);

  useEffect(() => {
    if (!viewStatus) return;
    const timer = window.setTimeout(() => setViewStatus(null), 2200);
    return () => window.clearTimeout(timer);
  }, [viewStatus]);

  // Restore-from-server: se localStorage e' vuoto (es. WebView ha
  // flushato cache) ma il server ha un backup, ripristiniamo le viste
  // perse. Esegue una sola volta al mount.
  useEffect(() => {
    if (savedViews.length > 0) return;
    let aborted = false;
    api
      .getSavedViewsBackup()
      .then((res) => {
        if (aborted || !res.payload) return;
        try {
          const parsed = JSON.parse(res.payload);
          const restored = parseSavedViewsList(parsed);
          if (restored.length === 0) return;
          if (typeof window !== "undefined") {
            try {
              window.localStorage.setItem(
                SAVED_VIEWS_STORAGE_KEY,
                JSON.stringify(restored),
              );
            } catch {
              // best-effort
            }
          }
          setSavedViews(restored);
          setViewStatus(
            `${restored.length} ${restored.length === 1 ? "vista ripristinata" : "viste ripristinate"} dal backup`,
          );
        } catch {
          // payload non parsabile: ignora
        }
      })
      .catch(() => {
        // backend non raggiungibile: silenzioso
      });
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (products.length === 0) {
    return (
      <div className="panel overflow-hidden">
        <div
          style={{ minWidth: selectionTableMinWidth }}
          role="table"
          aria-label="Prodotti marketplace"
        >
          <TableHead
            columns={visibleColumns}
            widths={columnLayout.widths}
            autoFit={autoFitWidths}
            gridTemplate={selectionGridTemplate}
            filters={filters}
            filterOptions={filterOptions}
            openFilter={openFilter}
            selectable={selectable}
            allVisibleSelected={allVisibleSelected}
            selectedVisibleCount={selectedVisibleCount}
            onToggleVisibleSelection={toggleVisibleSelection}
            onOpenFilter={setOpenFilter}
            onFilterQueryChange={setFilterQuery}
            onToggleFilterValue={toggleFilterValue}
            onSelectFilterValues={selectFilterValues}
            onClearColumnFilter={clearColumnFilter}
            onColumnWidthChange={setColumnWidth}
            onColumnWidthReset={resetColumnWidth}
            onReorderColumn={reorderColumn}
          />
        </div>
        <div className="empty-state py-12 text-center text-sm text-muted-foreground">
          Nessun prodotto. Importa un CSV per iniziare.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="panel flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="text-sm text-muted-foreground">
          <span className="font-mono text-xl font-semibold text-foreground">{filteredProducts.length}</span> di{" "}
          <span className="font-mono text-foreground">{products.length}</span> prodotti
          {selectable && selectedProducts.length > 0 ? (
            <span className="ml-2 font-mono text-xs text-primary">
              {selectedProducts.length} selezionati
            </span>
          ) : null}
          {viewStatus ? (
            <span className="ml-2 inline-flex items-center gap-1 text-accent-ink">
              <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              {viewStatus}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setViewsMenuOpen((open) => !open);
                setColumnMenuOpen(false);
                setOpenFilter(null);
              }}
              className="btn-editorial-outline min-h-9 gap-1 px-3 py-2 text-xs"
              aria-label="Gestisci viste salvate"
              aria-expanded={viewsMenuOpen}
              title="Salva, carica o elimina viste nominate"
            >
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
              Viste
              {savedViews.length > 0 ? (
                <span className="font-mono text-label text-muted-foreground">
                  {savedViews.length}
                </span>
              ) : null}
            </button>
            {viewsMenuOpen ? (
              <SavedViewsMenu
                views={savedViews}
                activeId={activeViewId}
                onSave={saveCurrentAsView}
                onUpdateActive={updateActiveView}
                onLoad={loadView}
                onDelete={deleteView}
                onRename={renameView}
                onPersistAsDefault={persistCurrentAsDefault}
                onClose={() => setViewsMenuOpen(false)}
              />
            ) : null}
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setColumnMenuOpen((open) => !open);
                setOpenFilter(null);
              }}
              className="btn-editorial-outline min-h-9 gap-1 px-3 py-2 text-xs"
              aria-label="Configura colonne prodotti"
              aria-expanded={columnMenuOpen}
            >
              <Columns3 className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
              Colonne
              <span className="font-mono text-label text-muted-foreground">
                {visibleColumns.length}/{COLUMNS.length}
              </span>
            </button>
            {columnMenuOpen ? (
              <ColumnSettingsMenu
                columns={orderedColumns}
                hiddenColumns={hiddenColumns}
                onMoveColumn={moveColumn}
                onToggleColumn={toggleColumnVisibility}
                onReset={resetColumnLayout}
              />
            ) : null}
          </div>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasFilters}
            className="btn-editorial-outline min-h-9 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
            Reset filtri
          </button>
          {selectable ? (
            <button
              type="button"
              onClick={clearSelection}
              disabled={selectedProducts.length === 0}
              className="btn-editorial-outline min-h-9 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
              Svuota selezione
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => downloadProductsExcel(filteredProducts, visibleColumns)}
            disabled={filteredProducts.length === 0}
            className="btn-editorial-primary min-h-9 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
            Scarica Excel
          </button>
        </div>
      </div>
      <ProductsGrid
        products={filteredProducts}
        columns={visibleColumns}
        widths={columnLayout.widths}
        autoFit={autoFitWidths}
        gridTemplate={selectionGridTemplate}
        tableMinWidth={selectionTableMinWidth}
        filters={filters}
        filterOptions={filterOptions}
        openFilter={openFilter}
        selectable={selectable}
        selectedSkus={selectedSkus}
        allVisibleSelected={allVisibleSelected}
        selectedVisibleCount={selectedVisibleCount}
        onToggleVisibleSelection={toggleVisibleSelection}
        onToggleProductSelection={toggleProductSelection}
        onOpenFilter={setOpenFilter}
        onFilterQueryChange={setFilterQuery}
        onToggleFilterValue={toggleFilterValue}
        onSelectFilterValues={selectFilterValues}
        onClearColumnFilter={clearColumnFilter}
        onColumnWidthChange={setColumnWidth}
        onColumnWidthReset={resetColumnWidth}
        onReorderColumn={reorderColumn}
        emptyMessage={
          hasFilters
            ? "Nessun prodotto corrisponde ai filtri."
            : "Nessun prodotto. Importa un CSV per iniziare."
        }
      />
    </div>
  );
}

type GridProps = {
  columns: ColumnDef[];
  widths: Partial<Record<ColumnKey, number>>;
  autoFit?: Partial<Record<ColumnKey, number>>;
  gridTemplate: string;
  tableMinWidth: string;
  selectable: boolean;
  selectedSkus: Set<string>;
  allVisibleSelected: boolean;
  selectedVisibleCount: number;
  filters: Filters;
  filterOptions: Record<ColumnKey, FilterOption[]>;
  openFilter: ColumnKey | null;
  onToggleVisibleSelection: () => void;
  onToggleProductSelection: (sku: string) => void;
  onOpenFilter: (key: ColumnKey | null) => void;
  onFilterQueryChange: (key: ColumnKey, value: string) => void;
  onToggleFilterValue: (key: ColumnKey, value: string) => void;
  onSelectFilterValues: (key: ColumnKey, values: string[]) => void;
  onClearColumnFilter: (key: ColumnKey) => void;
  onColumnWidthChange: (key: ColumnKey, width: number) => void;
  onColumnWidthReset: (key: ColumnKey) => void;
  onReorderColumn: (source: ColumnKey, target: ColumnKey) => void;
};

function ProductsGrid({
  products,
  emptyMessage,
  ...grid
}: GridProps & { products: ProductOut[]; emptyMessage: string }): JSX.Element {
  // Ref+height per il ramo lista corta (il ramo virtualizzato ha i suoi
  // dentro VirtualizedList). Hook chiamati sempre, usati solo in quel ramo.
  const shortListRef = useRef<HTMLDivElement | null>(null);
  const shortListHeight = useViewportFillHeight(shortListRef);
  const {
    columns,
    widths,
    autoFit,
    gridTemplate,
    tableMinWidth,
    filters,
    filterOptions,
    openFilter,
    selectable,
    selectedSkus,
    allVisibleSelected,
    selectedVisibleCount,
    onToggleVisibleSelection,
    onToggleProductSelection,
    onOpenFilter,
    onFilterQueryChange,
    onToggleFilterValue,
    onSelectFilterValues,
    onClearColumnFilter,
    onColumnWidthChange,
    onColumnWidthReset,
    onReorderColumn,
  } = grid;

  if (products.length === 0) {
    return (
      <div className="panel overflow-hidden">
        <div style={{ minWidth: tableMinWidth }} role="table" aria-label="Prodotti marketplace">
          <TableHead
            columns={columns}
            widths={widths}
            autoFit={autoFit}
            gridTemplate={gridTemplate}
            filters={filters}
            filterOptions={filterOptions}
            openFilter={openFilter}
            selectable={selectable}
            allVisibleSelected={allVisibleSelected}
            selectedVisibleCount={selectedVisibleCount}
            onToggleVisibleSelection={onToggleVisibleSelection}
            onOpenFilter={onOpenFilter}
            onFilterQueryChange={onFilterQueryChange}
            onToggleFilterValue={onToggleFilterValue}
            onSelectFilterValues={onSelectFilterValues}
            onClearColumnFilter={onClearColumnFilter}
            onColumnWidthChange={onColumnWidthChange}
            onColumnWidthReset={onColumnWidthReset}
            onReorderColumn={onReorderColumn}
          />
        </div>
        <div className="empty-state py-12 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      </div>
    );
  }

  if (products.length < VIRTUAL_THRESHOLD) {
    return (
      <div
        ref={shortListRef}
        className="card-stack scrollbar-always relative overflow-auto bg-card"
        style={{ maxHeight: shortListHeight ?? "70vh" }}
      >
        <div style={{ minWidth: tableMinWidth }} role="table" aria-label="Prodotti marketplace">
          <div className="sticky top-0 z-30 bg-card">
            <TableHead
              columns={columns}
              widths={widths}
              autoFit={autoFit}
              gridTemplate={gridTemplate}
              filters={filters}
              filterOptions={filterOptions}
              openFilter={openFilter}
              selectable={selectable}
              allVisibleSelected={allVisibleSelected}
              selectedVisibleCount={selectedVisibleCount}
              onToggleVisibleSelection={onToggleVisibleSelection}
              onOpenFilter={onOpenFilter}
              onFilterQueryChange={onFilterQueryChange}
              onToggleFilterValue={onToggleFilterValue}
              onSelectFilterValues={onSelectFilterValues}
              onClearColumnFilter={onClearColumnFilter}
              onColumnWidthChange={onColumnWidthChange}
              onColumnWidthReset={onColumnWidthReset}
              onReorderColumn={onReorderColumn}
            />
          </div>
          <div role="rowgroup">
            {products.map((p, index) => (
              <ProductRow
                key={`${p.marketplace_id ?? "mp"}-${p.sku}`}
                product={p}
                columns={columns}
                gridTemplate={gridTemplate}
                selectable={selectable}
                selected={selectedSkus.has(p.sku)}
                index={index}
                onToggleSelection={onToggleProductSelection}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return <VirtualizedList products={products} {...grid} />;
}

function VirtualizedList({
  products,
  columns,
  widths,
  autoFit,
  gridTemplate,
  tableMinWidth,
  filters,
  filterOptions,
  openFilter,
  selectable,
  selectedSkus,
  allVisibleSelected,
  selectedVisibleCount,
  onToggleVisibleSelection,
  onToggleProductSelection,
  onOpenFilter,
  onFilterQueryChange,
  onToggleFilterValue,
  onSelectFilterValues,
  onClearColumnFilter,
  onColumnWidthChange,
  onColumnWidthReset,
  onReorderColumn,
}: Props & GridProps): JSX.Element {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: products.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  // Altezza ancorata alla viewport (NON 70vh fisso): il fondo del container
  // — dove vive la scrollbar orizzontale — deve restare sempre visibile.
  const fillHeight = useViewportFillHeight(parentRef);

  // Single scroll container per sticky-header + sticky-first-col: sia X
  // che Y vivono qui sopra. Header e prima colonna usano `position:sticky`
  // contro questo elemento.
  return (
    <div
      ref={parentRef}
      className="card-stack scrollbar-always relative overflow-auto bg-card"
      style={{ height: fillHeight ?? "70vh" }}
    >
      <div
        style={{ minWidth: tableMinWidth }}
        role="table"
        aria-label="Prodotti marketplace"
      >
        <div className="sticky top-0 z-30 bg-card">
          <TableHead
            columns={columns}
            widths={widths}
            autoFit={autoFit}
            gridTemplate={gridTemplate}
            filters={filters}
            filterOptions={filterOptions}
            openFilter={openFilter}
            selectable={selectable}
            allVisibleSelected={allVisibleSelected}
            selectedVisibleCount={selectedVisibleCount}
            onToggleVisibleSelection={onToggleVisibleSelection}
            onOpenFilter={onOpenFilter}
            onFilterQueryChange={onFilterQueryChange}
            onToggleFilterValue={onToggleFilterValue}
            onSelectFilterValues={onSelectFilterValues}
            onClearColumnFilter={onClearColumnFilter}
            onColumnWidthChange={onColumnWidthChange}
            onColumnWidthReset={onColumnWidthReset}
            onReorderColumn={onReorderColumn}
          />
        </div>
        <div
          role="rowgroup"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((vrow) => {
            const p = products[vrow.index];
            return (
              <ProductRow
                key={`${p.marketplace_id ?? "mp"}-${p.sku}`}
                product={p}
                columns={columns}
                gridTemplate={gridTemplate}
                selectable={selectable}
                selected={selectedSkus.has(p.sku)}
                index={vrow.index}
                onToggleSelection={onToggleProductSelection}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  transform: `translateY(${vrow.start}px)`,
                  height: `${ROW_HEIGHT}px`,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HeaderTooltip({ id, label, text }: { id: string; label: string; text: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        className="mt-0.5 inline-flex h-3.5 w-3.5 items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
        aria-label={`Spiegazione ${label}`}
        aria-describedby={id}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      >
        <Info className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        id={id}
        className={cn(
          "absolute left-0 top-5 z-50 w-56 rounded-lg border border-line bg-background p-2 text-label font-normal normal-case leading-snug tracking-normal text-foreground shadow-popover",
          open ? "block" : "sr-only",
        )}
      >
        {text}
      </span>
    </span>
  );
}

function TableHead({
  columns,
  widths,
  autoFit,
  gridTemplate,
  filters,
  filterOptions,
  openFilter,
  selectable,
  allVisibleSelected,
  selectedVisibleCount,
  onToggleVisibleSelection,
  onOpenFilter,
  onFilterQueryChange,
  onToggleFilterValue,
  onSelectFilterValues,
  onClearColumnFilter,
  onColumnWidthChange,
  onColumnWidthReset,
  onReorderColumn,
}: {
  columns: ColumnDef[];
  widths: Partial<Record<ColumnKey, number>>;
  autoFit?: Partial<Record<ColumnKey, number>>;
  gridTemplate: string;
  filters: Filters;
  filterOptions: Record<ColumnKey, FilterOption[]>;
  openFilter: ColumnKey | null;
  selectable: boolean;
  allVisibleSelected: boolean;
  selectedVisibleCount: number;
  onToggleVisibleSelection: () => void;
  onOpenFilter: (key: ColumnKey | null) => void;
  onFilterQueryChange: (key: ColumnKey, value: string) => void;
  onToggleFilterValue: (key: ColumnKey, value: string) => void;
  onSelectFilterValues: (key: ColumnKey, values: string[]) => void;
  onClearColumnFilter: (key: ColumnKey) => void;
  onColumnWidthChange: (key: ColumnKey, width: number) => void;
  onColumnWidthReset: (key: ColumnKey) => void;
  onReorderColumn: (source: ColumnKey, target: ColumnKey) => void;
}): JSX.Element {
  // Ref al bottone-trigger della colonna col filtro aperto: il FilterMenu
  // (reso in portal) ci calcola la posizione fixed. Uno solo aperto per volta.
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [draggingKey, setDraggingKey] = useState<ColumnKey | null>(null);
  return (
    <div
      className={cn(
        "grid border-b border-line-2 bg-paper-2 text-xs font-medium text-muted-foreground",
        // Sticky first column anche nell'header (z superiore alla prima
        // cella riga per restare sopra durante scroll incrociato). Con
        // selectable si estende a checkbox + SKU come nelle righe.
        selectable
          ? "[&>*:nth-child(-n+2)]:sticky [&>*:nth-child(-n+2)]:z-20 [&>*:nth-child(-n+2)]:bg-paper-2 [&>*:first-child]:left-0 [&>*:nth-child(2)]:left-[48px]"
          : "[&>*:first-child]:sticky [&>*:first-child]:left-0 [&>*:first-child]:z-20 [&>*:first-child]:bg-paper-2",
      )}
      role="row"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {selectable ? (
        <div
          className="flex min-w-0 items-center justify-center px-2 py-2"
          role="columnheader"
        >
          <button
            type="button"
            onClick={onToggleVisibleSelection}
            disabled={filterOptions.sku.length === 0}
            aria-label={
              allVisibleSelected ? "Deseleziona prodotti visibili" : "Seleziona prodotti visibili"
            }
            title={
              selectedVisibleCount > 0
                ? `${selectedVisibleCount} visibili selezionati`
                : "Seleziona prodotti visibili"
            }
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded border",
              allVisibleSelected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-line bg-background text-muted-foreground",
            )}
          >
            {allVisibleSelected ? <Check className="h-4 w-4" strokeWidth={2} /> : null}
          </button>
        </div>
      ) : null}
      {columns.map((column) => (
        <div
          key={column.key}
          className={cn(
            "relative flex min-w-0 flex-col gap-1 px-3 py-2",
            column.align === "right" ? "items-end text-right" : "items-start text-left",
          )}
          role="columnheader"
          style={groupHeaderStyle(column.group)}
          data-group={column.group}
          onDragOver={(e) => {
            if (draggingKey && draggingKey !== column.key) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            const src = e.dataTransfer.getData("text/plain") as ColumnKey;
            if (src) onReorderColumn(src, column.key);
            setDraggingKey(null);
          }}
        >
          {/* flex-wrap: sotto il min-content (colonna ristretta a mano) la ⓘ
              scende sotto il chip invece di sbordare sulla colonna adiacente */}
          <div className="relative flex w-full flex-wrap items-start gap-1">
            <button
              type="button"
              draggable
              aria-label={`Trascina colonna ${column.label}`}
              className="flex h-9 w-4 shrink-0 cursor-move items-center justify-center text-muted-foreground hover:text-foreground"
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", column.key);
                setDraggingKey(column.key);
              }}
              onDragEnd={() => setDraggingKey(null)}
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
            </button>
            <button
              type="button"
              ref={openFilter === column.key ? filterTriggerRef : null}
              onClick={() => onOpenFilter(openFilter === column.key ? null : column.key)}
              className={cn(
                "control-shell flex min-h-9 flex-1 items-center justify-between gap-2 px-2.5 py-1.5 text-left text-label font-medium normal-case tracking-normal text-foreground shadow-none hover:bg-white",
                isColumnFiltered(filters[column.key]) &&
                  "border-key-press bg-key/30 font-semibold text-ink",
              )}
              aria-label={`Filtro ${column.label}`}
              aria-expanded={openFilter === column.key}
            >
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <ListFilter className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                {/* line-clamp-2: wrap max 2 righe + ellissi e CLIP — senza,
                    il testo overflowa il chip e si sormonta a ⓘ/chevron
                    quando la colonna è più stretta del min-content (bug
                    2026-07-02). Nome completo nel title e nel tooltip ⓘ. */}
                <span
                  className="line-clamp-2 min-w-0 flex-1 break-normal leading-[1.15] tracking-normal"
                  title={column.label}
                >
                  {column.label}
                </span>
                {filters[column.key].selected.length > 0 ? (
                  <span className="shrink-0 font-mono text-label tracking-normal">
                    {filters[column.key].selected.length}
                  </span>
                ) : null}
              </span>
              <ChevronDown
                className="h-3.5 w-3.5 shrink-0"
                strokeWidth={1.5}
                aria-hidden="true"
              />
            </button>
            <HeaderTooltip
              id={`tip-${column.key}`}
              label={column.label}
              text={column.tooltip}
            />
            {openFilter === column.key ? (
              <FilterMenu
                column={column}
                filter={filters[column.key]}
                options={filterOptions[column.key]}
                anchorRef={filterTriggerRef}
                onQueryChange={(value) => onFilterQueryChange(column.key, value)}
                onToggleValue={(value) => onToggleFilterValue(column.key, value)}
                onSelectValues={(values) => onSelectFilterValues(column.key, values)}
                onClear={() => onClearColumnFilter(column.key)}
                onClose={() => onOpenFilter(null)}
              />
            ) : null}
          </div>
          <ColumnResizeHandle
            columnKey={column.key}
            currentWidth={resolveWidth(column.key, widths, autoFit)}
            onChange={onColumnWidthChange}
            onReset={onColumnWidthReset}
          />
        </div>
      ))}
    </div>
  );
}

function ColumnResizeHandle({
  columnKey,
  currentWidth,
  onChange,
  onReset,
}: {
  columnKey: ColumnKey;
  currentWidth: number;
  onChange: (key: ColumnKey, width: number) => void;
  onReset: (key: ColumnKey) => void;
}): JSX.Element {
  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = currentWidth;
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      target.dataset.resizing = "true";

      const move = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        onChange(columnKey, startWidth + delta);
      };
      const up = (ev: PointerEvent) => {
        target.releasePointerCapture(ev.pointerId);
        delete target.dataset.resizing;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [columnKey, currentWidth, onChange],
  );

  return (
    <div
      role="separator"
      aria-label={`Ridimensiona colonna ${columnKey}`}
      aria-orientation="vertical"
      onPointerDown={handlePointerDown}
      onDoubleClick={() => onReset(columnKey)}
      className="group absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize touch-none items-center justify-center select-none"
      title="Trascina per ridimensionare, doppio click per reset"
    >
      <span
        className="h-6 w-[2px] rounded bg-line transition-colors group-hover:bg-primary group-data-[resizing=true]:bg-primary"
        aria-hidden="true"
      />
    </div>
  );
}

function SavedViewsMenu({
  views,
  activeId,
  onSave,
  onUpdateActive,
  onLoad,
  onDelete,
  onRename,
  onPersistAsDefault,
  onClose,
}: {
  views: SavedView[];
  activeId: string | null;
  onSave: (name: string) => void;
  onUpdateActive: () => void;
  onLoad: (view: SavedView) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onPersistAsDefault: () => void;
  onClose: () => void;
}): JSX.Element {
  const [draftName, setDraftName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleSave = () => {
    if (!draftName.trim()) return;
    onSave(draftName);
    setDraftName("");
  };

  const commitRename = (id: string) => {
    if (editingName.trim()) onRename(id, editingName);
    setEditingId(null);
    setEditingName("");
  };

  return (
    <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-80 rounded-xl border border-line bg-background p-3 text-popover-foreground shadow-popover">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-line pb-3">
        <div>
          <div className="font-semibold tracking-tight text-lg text-foreground">Viste salvate</div>
          <div className="text-label text-muted-foreground">
            {views.length} {views.length === 1 ? "vista" : "viste"}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-paper hover:text-foreground"
          aria-label="Chiudi menu viste"
        >
          <X className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <label className="text-label text-muted-foreground">
          Salva vista corrente
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSave();
              }
            }}
            placeholder="Nome vista (es. 'Out buybox IT')"
            className="control-shell min-h-9 flex-1 px-2.5 text-xs font-normal normal-case text-foreground outline-none placeholder:text-muted-foreground/70"
            aria-label="Nome nuova vista"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!draftName.trim()}
            className="btn-editorial-primary min-h-9 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
            Salva
          </button>
        </div>
        <button
          type="button"
          onClick={onPersistAsDefault}
          className="btn-editorial-outline min-h-8 w-full justify-center px-2.5 py-1 text-label"
          title="Salva questa disposizione come predefinita al prossimo avvio"
        >
          <Save className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
          Salva come predefinita
        </button>
        <button
          type="button"
          onClick={onUpdateActive}
          disabled={!activeId}
          className="btn-editorial-outline min-h-8 w-full justify-center px-2.5 py-1 text-label disabled:cursor-not-allowed disabled:opacity-50"
          title={
            activeId
              ? "Sovrascrive la vista attiva con i filtri/colonne attuali"
              : "Carica prima una vista per poterla aggiornare"
          }
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
          Aggiorna vista corrente
        </button>
      </div>

      {views.length > 0 ? (
        <div className="scrollbar-thin max-h-72 space-y-1 overflow-auto border-t border-line pt-2">
          {views.map((view) => {
            const isActive = view.id === activeId;
            const isEditing = editingId === view.id;
            return (
              <div
                key={view.id}
                className={cn(
                  "grid min-h-10 grid-cols-[1fr_auto] items-center gap-2 rounded-lg border border-line bg-paper px-2.5 py-1.5 normal-case",
                  isActive && "border-primary/60 bg-primary/10",
                )}
              >
                {isEditing ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(event) => setEditingName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitRename(view.id);
                      } else if (event.key === "Escape") {
                        setEditingId(null);
                        setEditingName("");
                      }
                    }}
                    onBlur={() => commitRename(view.id)}
                    autoFocus
                    className="control-shell min-h-8 w-full px-2 text-xs font-normal normal-case text-foreground outline-none"
                    aria-label="Rinomina vista"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onLoad(view)}
                    onDoubleClick={() => {
                      setEditingId(view.id);
                      setEditingName(view.name);
                    }}
                    className="flex min-w-0 flex-col items-start gap-0.5 text-left text-xs font-medium"
                    title="Click: carica | Doppio click: rinomina"
                  >
                    <span className="truncate text-foreground">{view.name}</span>
                    <span className="font-mono text-micro text-muted-foreground">
                      {new Date(view.createdAt).toLocaleString("it-IT", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Eliminare la vista "${view.name}"?`)) onDelete(view.id);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-background text-muted-foreground hover:bg-paper hover:text-destructive"
                  aria-label={`Elimina vista ${view.name}`}
                  title="Elimina vista"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-label normal-case text-muted-foreground">
          Nessuna vista salvata. Assegna un nome e salva la configurazione corrente.
        </div>
      )}
    </div>
  );
}

function ColumnSettingsMenu({
  columns,
  hiddenColumns,
  onMoveColumn,
  onToggleColumn,
  onReset,
}: {
  columns: ColumnDef[];
  hiddenColumns: Set<ColumnKey>;
  onMoveColumn: (key: ColumnKey, direction: -1 | 1) => void;
  onToggleColumn: (key: ColumnKey) => void;
  onReset: () => void;
}): JSX.Element {
  const visibleCount = columns.filter((column) => !hiddenColumns.has(column.key)).length;

  return (
    <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-80 rounded-xl border border-line bg-background p-3 text-popover-foreground shadow-popover">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-line pb-3">
        <div>
          <div className="font-semibold tracking-tight text-lg text-foreground">Disposizione colonne</div>
          <div className="text-label text-muted-foreground">
            {visibleCount}/{columns.length} visibili
          </div>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="btn-editorial-outline min-h-8 px-2.5 py-1 text-xs"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
          Reset
        </button>
      </div>
      <div className="scrollbar-thin max-h-96 space-y-1 overflow-auto">
        {columns.map((column, index) => {
          const hidden = hiddenColumns.has(column.key);
          const disableHide = !hidden && visibleCount <= 1;
          return (
            <div
              key={column.key}
              className={cn(
                "grid min-h-10 grid-cols-[1fr_auto] items-center gap-2 rounded-lg border border-line bg-paper px-2.5 py-1.5",
                hidden && "bg-background text-muted-foreground",
              )}
            >
              <button
                type="button"
                onClick={() => onToggleColumn(column.key)}
                disabled={disableHide}
                className="flex min-w-0 items-center gap-2 text-left text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                aria-pressed={!hidden}
                aria-label={`${hidden ? "Mostra" : "Nascondi"} colonna ${column.label}`}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                    hidden
                      ? "border-line bg-background"
                      : "border-primary bg-primary text-primary-foreground",
                  )}
                  aria-hidden="true"
                >
                  {hidden ? (
                    <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                  )}
                </span>
                <span className="truncate">{column.label}</span>
              </button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onMoveColumn(column.key, -1)}
                  disabled={index === 0}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-background hover:bg-paper disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Sposta ${column.label} a sinistra`}
                >
                  <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => onMoveColumn(column.key, 1)}
                  disabled={index === columns.length - 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-background hover:bg-paper disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Sposta ${column.label} a destra`}
                >
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterMenu({
  column,
  filter,
  options,
  anchorRef,
  onQueryChange,
  onToggleValue,
  onSelectValues,
  onClear,
  onClose,
}: {
  column: ColumnDef;
  filter: ColumnFilter;
  options: FilterOption[];
  anchorRef: RefObject<HTMLButtonElement | null>;
  onQueryChange: (value: string) => void;
  onToggleValue: (value: string) => void;
  onSelectValues: (values: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}): JSX.Element | null {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Posizione `fixed` calcolata dal trigger e clampata al viewport. Il menu e'
  // reso in un portal su document.body per NON essere tagliato dal contenitore
  // con scroll orizzontale / `contain` della tabella (bug clipping filtri).
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (anchor === null) return;
    const rect = anchor.getBoundingClientRect();
    const MENU_W = 288; // w-72
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - MENU_W - 8);
    setPos({ top: rect.bottom + 8, left });
  }, [anchorRef]);

  // Su scroll (qualsiasi container) o resize il menu fixed resterebbe staccato
  // dalla colonna: lo chiudiamo. Il listener e' in capture per intercettare
  // anche gli scroll container interni della tabella, che non bollono fino a
  // window — ma cosi' vede pure lo scroll DENTRO il menu: senza la guardia,
  // scorrere la lista valori la chiudeva all'istante (filtri con molti valori,
  // es. Fornitore, diventavano inutilizzabili).
  useEffect(() => {
    const onScrollOutside = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    window.addEventListener("scroll", onScrollOutside, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onScrollOutside, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  // Click fuori dal menu e dal trigger => chiudi.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [anchorRef, onClose]);

  const visibleOptions = options.filter((option) =>
    normalize(option.label).includes(normalize(filter.query)),
  );
  const selected = new Set(filter.selected);

  if (pos === null) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: "fixed", top: pos.top, left: pos.left }}
      className="z-50 w-72 rounded-xl border border-line bg-background p-3 text-popover-foreground shadow-popover"
      role="dialog"
      aria-label={`Filtro ${column.label}`}
    >
      <div className="control-shell mb-3 flex items-center gap-2 px-2.5">
        <Search
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <input
          type="text"
          value={filter.query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={column.placeholder}
          className="min-h-8 flex-1 bg-transparent text-xs font-normal normal-case text-foreground outline-none placeholder:text-muted-foreground/70"
          aria-label={`Cerca in ${column.label}`}
        />
        {filter.query ? (
          <button
            type="button"
            onClick={() => onQueryChange("")}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={`Cancella ricerca ${column.label}`}
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="mb-3 flex items-center justify-between gap-2 text-label font-medium normal-case">
        <button
          type="button"
          onClick={() => onSelectValues(visibleOptions.map((option) => option.value))}
          className="rounded px-2 py-1 text-primary hover:bg-primary/10"
        >
          Seleziona visibili
        </button>
        <button type="button" onClick={onClear} className="rounded px-2 py-1 hover:bg-paper">
          Svuota filtro
        </button>
      </div>
      <div className="scrollbar-thin max-h-64 overflow-auto rounded-lg border border-line bg-paper">
        {visibleOptions.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs normal-case text-muted-foreground">
            Nessun valore
          </div>
        ) : (
          visibleOptions.map((option) => (
            <label
              key={`${column.key}-${option.value}`}
              className="flex min-h-8 cursor-pointer items-center gap-2 border-b border-line/70 px-2 py-1.5 last:border-b-0 hover:bg-background"
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  selected.has(option.value)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-line bg-background",
                )}
                aria-hidden="true"
              >
                {selected.has(option.value) ? <Check className="h-3 w-3" strokeWidth={2} /> : null}
              </span>
              <input
                type="checkbox"
                checked={selected.has(option.value)}
                onChange={() => onToggleValue(option.value)}
                className="sr-only"
              />
              <span className="min-w-0 flex-1 truncate text-left text-xs font-normal normal-case text-foreground">
                {option.label || "(vuoto)"}
              </span>
              <span className="font-mono text-label font-normal text-muted-foreground">
                {option.count}
              </span>
            </label>
          ))
        )}
      </div>
    </div>,
    document.body,
  );
}

function ProductRow({
  product,
  columns,
  gridTemplate,
  selectable,
  selected,
  index,
  onToggleSelection,
  style,
}: {
  product: ProductOut;
  columns: ColumnDef[];
  gridTemplate: string;
  selectable: boolean;
  selected: boolean;
  index: number;
  onToggleSelection: (sku: string) => void;
  style?: CSSProperties;
}): JSX.Element {
  // Zebra striping: righe pari = sfondo neutro, dispari = paper sottile.
  // Hover override entrambi via gruppo. La prima cella usa `bg-inherit`
  // così la sticky-left mostra sempre il colore della riga corrente.
  // Tabella bianca con righe alternate: pari = card (#FFF), dispari =
  // paper-2 (#f3f1ea) per contrasto leggibile sul paper di sfondo.
  const zebraBg = index % 2 === 0 ? "bg-card" : "bg-paper-2";
  return (
    <div
      className={cn(
        "group grid border-b border-line/70 text-sm",
        zebraBg,
        "hover:bg-key/15",
        // Sticky first column: resta visibile durante lo scroll orizzontale,
        // `bg-inherit` mantiene la zebra. Con selectable la PRIMA cella è la
        // checkbox (48px): senza il nth-child(2) lo SKU scorrerebbe via e la
        // colonna "bloccata" sarebbe solo la checkbox (bug 2026-07-02).
        selectable
          ? "[&>*:nth-child(-n+2)]:sticky [&>*:nth-child(-n+2)]:z-10 [&>*:nth-child(-n+2)]:bg-inherit [&>*:first-child]:left-0 [&>*:nth-child(2)]:left-[48px] [&>*:nth-child(2)]:border-r [&>*:nth-child(2)]:border-line/70"
          : "[&>*:first-child]:sticky [&>*:first-child]:left-0 [&>*:first-child]:z-10 [&>*:first-child]:bg-inherit [&>*:first-child]:border-r [&>*:first-child]:border-line/70",
      )}
      role="row"
      style={{ ...style, gridTemplateColumns: gridTemplate }}
    >
      {selectable ? (
        <div className="flex items-center justify-center border-r border-line/70 px-2" role="cell">
          <button
            type="button"
            onClick={() => onToggleSelection(product.sku)}
            aria-label={`${selected ? "Deseleziona" : "Seleziona"} ${product.sku}`}
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded border",
              selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-line bg-background text-muted-foreground hover:border-primary/50",
            )}
          >
            {selected ? <Check className="h-4 w-4" strokeWidth={2} /> : null}
          </button>
        </div>
      ) : null}
      <ProductCells product={product} columns={columns} />
    </div>
  );
}

function ProductCells({
  product,
  columns,
}: {
  product: ProductOut;
  columns: ColumnDef[];
}): JSX.Element {
  return (
    <>
      {columns.map((column) => (
        <ProductCell key={column.key} product={product} column={column} />
      ))}
    </>
  );
}

function ProductCell({ product, column }: { product: ProductOut; column: ColumnDef }): JSX.Element {
  const competitorOffer = product.min_competitor_price;
  const competitorTotal = product.min_competitor_total;
  const competitorName = product.min_competitor_shop_name ?? product.min_competitor_shop_id;
  const feeMeta = feeConfidenceMeta(product);
  const belowMargin = isBelowMargin(product);
  const competitorLabel =
    competitorOffer && competitorTotal && competitorOffer !== competitorTotal
      ? `${formatPrice(competitorOffer)} / tot. ${formatPrice(competitorTotal)}`
      : formatPrice(competitorOffer ?? competitorTotal);
  switch (column.key) {
    case "sku":
      return (
        <div className={cellClassName("left")} role="cell">
          <SkuIdentity product={product} />
        </div>
      );
    case "ean":
      return (
        <div
          className={cellClassName("left", "font-mono text-xs text-muted-foreground")}
          role="cell"
          title={product.ean ?? undefined}
        >
          {product.ean}
        </div>
      );
    case "brand":
      return (
        <div className={cellClassName("left")} role="cell">
          {product.supplier ?? product.brand ?? "-"}
        </div>
      );
    case "vendor_brand":
      return (
        <div className={cellClassName("left")} role="cell">
          {product.vendor_brand ?? "-"}
        </div>
      );
    case "landed_cost":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.landed_cost)}
        </div>
      );
    case "list_cost":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.list_cost)}
        </div>
      );
    case "discount_cost":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.discount_cost)}
        </div>
      );
    case "shipping_estimate":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.shipping_estimate)}
        </div>
      );
    case "shipping_cost_certified":
      return (
        <div
          className={cellClassName("right", "font-mono tabular-nums")}
          role="cell"
          title={
            (product.shipping_cost_certified != null
              ? "Costo confermato dalle fatture del corriere (Bracchi)"
              : "Non ancora confermato dalle fatture corriere") +
            courierBreakdownSuffix(product.shipping_by_courier)
          }
        >
          {formatPrice(product.shipping_cost_certified)}
        </div>
      );
    case "shipping_cost_effective":
      return (
        <div
          className={cellClassName("right", "font-mono tabular-nums")}
          role="cell"
          title={
            SHIPPING_SOURCE_TITLE[product.shipping_cost_source] +
            (product.shipping_cost_source === "certified"
              ? courierBreakdownSuffix(product.shipping_by_courier)
              : "")
          }
        >
          {product.shipping_cost_effective == null ? (
            <span className="font-sans text-xs text-danger">
              {SHIPPING_SOURCE_LABEL.none}
            </span>
          ) : (
            <div className="min-w-0">
              <div>{formatPrice(product.shipping_cost_effective)}</div>
              <div className="mt-0.5 truncate font-sans text-label text-muted-foreground">
                {SHIPPING_SOURCE_LABEL[product.shipping_cost_source]}
              </div>
            </div>
          )}
        </div>
      );
    case "current_fee_cost":
      return (
        <div
          className={cellClassName(
            "right",
            cn("font-mono tabular-nums", product.current_fee_cost != null && feeMeta.className),
          )}
          role="cell"
          title={product.current_fee_cost != null ? feeMeta.title : undefined}
        >
          {formatPrice(product.current_fee_cost)}
        </div>
      );
    case "current_total_cost":
      return (
        <div
          className={cellClassName(
            "right",
            cn("font-mono tabular-nums", product.current_total_cost != null && feeMeta.className),
          )}
          role="cell"
          title={product.current_total_cost != null ? feeMeta.title : undefined}
        >
          {formatPrice(product.current_total_cost)}
        </div>
      );
    case "margin_floor":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.margin_floor)}
        </div>
      );
    case "my_price":
      return (
        <div
          className={cellClassName(
            "right",
            cn("font-mono tabular-nums", belowMargin && "text-danger"),
          )}
          role="cell"
          title={
            product.price_locked
              ? "Prezzo bloccato a mano: l'auto-prezzo non lo modifica"
              : belowMargin
                ? "Prezzo sotto il margine minimo: andrebbe alzato al floor di margine"
                : undefined
          }
        >
          {formatPrice(product.my_total_price ?? product.my_price)}
          {product.price_locked && (
            <Lock
              className="ml-1 inline-block h-3 w-3 shrink-0 text-muted-foreground"
              strokeWidth={1.8}
              aria-label="Prezzo bloccato"
            />
          )}
          {belowMargin && (
            <AlertTriangle
              className="ml-1 inline-block h-3 w-3 shrink-0"
              strokeWidth={1.8}
              aria-hidden="true"
            />
          )}
        </div>
      );
    case "current_margin":
      return (
        <div
          className={cellClassName(
            "right",
            cn("font-mono tabular-nums", belowMargin && "text-danger"),
          )}
          role="cell"
        >
          <MarginValue
            amount={product.current_margin}
            percent={product.current_margin_percent}
            toneClassName={belowMargin ? undefined : feeMeta.className}
            title={
              belowMargin
                ? "Prezzo sotto il margine minimo: andrebbe alzato al floor di margine"
                : feeMeta.title
            }
          />
          {belowMargin && (
            <AlertTriangle
              className="ml-1 inline-block h-3 w-3 shrink-0"
              strokeWidth={1.8}
              aria-hidden="true"
            />
          )}
        </div>
      );
    case "guadagno_eur": {
      const gain = product.current_margin != null ? Number(product.current_margin) : null;
      const lossClass = gain != null && gain < 0 ? "text-danger" : "";
      return (
        <div
          className={cellClassName("right", cn("font-mono tabular-nums", lossClass))}
          role="cell"
          title={gain != null && gain < 0 ? "In perdita al prezzo attuale" : undefined}
        >
          {formatPrice(product.current_margin)}
        </div>
      );
    }
    case "competitor_offer":
      return (
        <div
          className={cellClassName("right", "font-mono tabular-nums")}
          role="cell"
          title={competitorName ? `Shop competitor: ${competitorName}` : undefined}
        >
          <div className="min-w-0">
            <div>{competitorLabel}</div>
            {competitorName ? (
              <div className="truncate text-label text-muted-foreground">{competitorName}</div>
            ) : null}
          </div>
        </div>
      );
    case "buybox_target_price":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.buybox_target_price)}
        </div>
      );
    case "buybox_target_margin":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          <MarginValue
            amount={product.buybox_target_margin}
            percent={product.buybox_target_margin_percent}
            toneClassName={feeMeta.className}
            title={feeMeta.title}
          />
        </div>
      );
    case "competitor_count":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {product.competitor_count ?? "-"}
        </div>
      );
    case "buybox_state":
      return (
        <div className={cellClassName("left")} role="cell">
          <div className="min-w-0">
            <StatusBadge product={product} />
            {buyboxOutcome(product) === "lost" && competitorName ? (
              <div className="mt-0.5 truncate text-label text-muted-foreground">
                {competitorName}
              </div>
            ) : null}
          </div>
        </div>
      );
    case "last_checked_at":
      return (
        <div className={cellClassName("left", "text-xs text-muted-foreground")} role="cell">
          {formatDateTime(product.last_checked_at)}
        </div>
      );
    case "price_at_target_margin":
      return (
        <div className={cellClassName("right", "font-mono tabular-nums")} role="cell">
          {formatPrice(product.price_at_target_margin)}
        </div>
      );
    default:
      return (
        <div className={cellClassName(column.align)} role="cell">
          -
        </div>
      );
  }
}

function SkuIdentity({ product }: { product: ProductOut }): JSX.Element {
  const [imageMissing, setImageMissing] = useState(false);
  const imageUrl = api.productThumbnailUrl(product.sku);
  const subtitle = product.name ?? product.brand ?? product.marketplace_label ?? null;
  const title = subtitle ? `${product.sku}\n${subtitle}` : product.sku;
  return (
    <div className="flex min-w-0 items-center gap-2.5" title={title}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-paper">
        {imageMissing ? (
          <ImageOff
            className="h-4 w-4 text-muted-foreground"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        ) : (
          <img
            src={imageUrl}
            alt={`Miniatura SKU ${product.sku}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            onError={() => setImageMissing(true)}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-code text-data text-foreground">{product.sku}</div>
        {subtitle ? (
          <div className="truncate text-label text-muted-foreground">{subtitle}</div>
        ) : null}
      </div>
    </div>
  );
}

function MarginValue({
  amount,
  percent,
  toneClassName,
  title,
}: {
  amount: string | number | null | undefined;
  percent: string | number | null | undefined;
  toneClassName?: string;
  title?: string;
}): JSX.Element {
  const hasValue = amount !== null && amount !== undefined && amount !== "";

  return (
    <span className={cn(hasValue && toneClassName)} title={hasValue ? title : undefined}>
      {formatPrice(amount)}
      <span className="ml-1 text-label opacity-80">{formatPercent(percent)}</span>
    </span>
  );
}

// Fonte della spedizione usata dal prezzo, nell'ordine di `effective_shipping`.
const SHIPPING_SOURCE_LABEL: Record<ProductOut["shipping_cost_source"], string> = {
  certified: "fattura",
  computed: "calcolo PIM",
  estimate: "listino",
  brt: "BRT listino",
  none: "manca",
};

const SHIPPING_SOURCE_TITLE: Record<ProductOut["shipping_cost_source"], string> = {
  certified: "Mediana delle fatture del corriere",
  computed: "Calcolata da peso e misure del PIM col tariffario Poste",
  estimate: "Stima del listino fornitore: mancano fattura e calcolo PIM",
  brt: "Stima BRT del listino fornitore: all'estero vale solo questa",
  none: "Nessuna spedizione: senza, il prezzo non si calcola",
};

function courierBreakdownSuffix(
  byCourier: Record<string, { cost: string; n: number }> | undefined,
): string {
  if (!byCourier) return "";
  const parts = Object.entries(byCourier).map(
    ([courier, v]) => `${courier.toUpperCase()}: ${formatPrice(v.cost)} (${v.n} sped.)`,
  );
  return parts.length ? `\n— Per corriere: ${parts.join(" · ")}` : "";
}

function feeConfidenceMeta(product: ProductOut): { className: string; title: string } {
  const rateLabel = formatCommissionRate(product.commission_rate);
  if (product.commission_source === "orders" && product.commission_sample_size > 0) {
    const label =
      product.commission_sample_size === 1 ? "1 ordine Mirakl" : `${product.commission_sample_size} ordini Mirakl`;
    return {
      className: "text-accent-ink",
      title: `SKU verificato da ${label} - fee marketplace ${rateLabel}`,
    };
  }
  return {
    className: "text-warn",
    title: `Fee marketplace stimata di fallback - ${rateLabel}`,
  };
}

function cellClassName(align: "left" | "right", extra = ""): string {
  const alignment = align === "right" ? "justify-end text-right" : "justify-start text-left";
  return `flex min-w-0 items-center overflow-hidden px-3 py-2 ${alignment} ${extra}`;
}

function productMatchesFilters(product: ProductOut, filters: Filters): boolean {
  return COLUMNS.every((column) => {
    const filter = filters[column.key];
    const query = normalize(filter.query);
    if (query && !normalize(column.filterValue(product)).includes(query)) return false;
    if (filter.selected.length === 0) return true;
    return filter.selected.includes(column.optionValue(product));
  });
}

function buildFilterOptions(products: ProductOut[]): Record<ColumnKey, FilterOption[]> {
  return Object.fromEntries(
    COLUMNS.map((column) => {
      const counts = new Map<string, number>();
      for (const product of products) {
        const value = column.optionValue(product);
        counts.set(value, (counts.get(value) ?? 0) + 1);
      }
      const options = Array.from(counts, ([value, count]) => ({
        value,
        label: value,
        count,
      })).sort((a, b) => a.label.localeCompare(b.label, "it", { numeric: true }));
      return [column.key, options];
    }),
  ) as Record<ColumnKey, FilterOption[]>;
}

function isColumnFiltered(filter: ColumnFilter): boolean {
  return filter.query.length > 0 || filter.selected.length > 0;
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function priceSearchValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  return `${value} ${formatPrice(value)}`;
}

function marginLabel(
  amount: string | number | null | undefined,
  percent: string | number | null | undefined,
): string {
  return `${formatPrice(amount)} ${formatPercent(percent)}`;
}

// Etichetta header del margine obiettivo: intero senza decimali superflui
// ("20" -> "20%", "20,5" -> "20,5%"). La % viene dal setting target_margin_pct.
function formatTargetMarginLabel(pct: string | number): string {
  const n = typeof pct === "string" ? Number(pct) : pct;
  if (Number.isNaN(n)) return "—";
  return Number.isInteger(n) ? `${n}%` : `${String(n).replace(".", ",")}%`;
}

function formatCommissionRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "fee n/d";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(numeric)) return "fee n/d";
  return `fee ${formatPercent(numeric * 100)}`;
}

function downloadProductsExcel(products: ProductOut[], columns: ColumnDef[]): void {
  const rows = [
    columns.map((column) => column.label),
    ...products.map((product) => columns.map((column) => column.exportValue(product))),
  ];
  const xmlRows = rows
    .map(
      (row) =>
        `<Row>${row
          .map((value) => `<Cell><Data ss:Type="String">${escapeXml(String(value))}</Data></Cell>`)
          .join("")}</Row>`,
    )
    .join("");
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Prodotti">
  <Table>${xmlRows}</Table>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });
  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `elenco_prodotti_${stamp}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
